// composables/useSwatches.js
import { ref, reactive, computed, onMounted, watchEffect } from 'vue'
import Engine from "~/composables/Engine.js"

export function useSwatches() {
  const runEngine = new Engine()
  const route = useRoute()
  
  // Core state refs
  const page = ref({ Products: { data: {} }, AOVBooster: [], PreselPack: null, ExtraData: { packRewards: [] } })
  const isPageLoaded = ref(false)
  const highlightCheckbox = ref(false)
  const checkboxChecked = ref(false)
  const productsList = computed(() => isSubscription.value ? ["7071544082492", "6970469613628", "8023271014460"] : ["6938980876348", "6980372004924", "7148455624764", "8023271014460"])
  const packRewards = computed(() => page?.value?.ExtraData?.packRewards);
  
  // Computed properties
  const products = computed(() => {
    if (!page.value?.Products) return {}
    return Object.keys(page.value.Products).reduce(
      (r, v) => ({ ...r, [v]: page.value.Products[v] }),
      {}
    )
  })
  const hasItems = computed(() => {
    return Object.keys(products.value).length > 0
  })
  const upgrades = computed(() =>
    page.value.AOVBooster.length ? page.value.AOVBooster[0] : []
  )

  const isSubscription = computed(() =>
    page.value.Subscription ?? false
  )
  

  const PreselectedPack = computed(() =>
    page.value.PreselPack !== null ? page.value.PreselPack - 1 : 0
  )
  const PreselectedVariant = computed(() =>
    page.value.PreselVar !== null ? page.value.PreselVar - 1 : 0
  )
  const PreselectedItem = computed(() =>
    page.value.PreselRoast !== null ? page.value.PreselRoast - 1 : 0
  )

  // Main state object
  const state = reactive({
    layout: ref(""),
    layoutPopup: ref(""),
    subscriptionPage: computed(() => isSubscription.value),
    pageSteps: ref({}),
    items: products,
    upgrades: upgrades,
    packRewards: packRewards,
    variants: computed(() => state.items[state.active.items]?.Variants || []),
    packs: products.value[0]?.Variants[0]?.Pack.filter((obj) => obj.Active) || [],
    images: products.value[0]?.Variants[0]?.variant.Gallery[0],
    unit: ref({
      beans: ["bag", "bags"],
      pods: ["box", "boxes"],
      ground: ["bag", "bags"],
      wholebeans: ["bag", "bags"],
      coffeeground: ["bag", "bags"],
      coffeebeans: ["bag", "bags"],
      coffeepods: ["box", "boxes"],
    }),
    currentUnit: ref(["bag", "bags"]),
    active: {
      items: 0,
      variants: 0,
      packs: PreselectedPack.value,
      sellingPlan: 3,
    },
    upgrade: ref({
      UpSell: {},
      CrossSell: {},
      show: false,
    }),
    isSubscription: computed(() => isSubscription.value),
    regular: ref({}),
    trackingStack: ref({}),
  })
  

  // Core methods
  const initializePage = async (salePage = null) => {
    try {
      page.value = await runEngine.queryPage(
        salePage || route.path.split("/").at(-1).split("?")[0]
      )
      isPageLoaded.value = true
      
      // Initialize preselected values after page loads
      if (page.value && Object.keys(products.value).length > 0) {
        // Set initial product
        const initialProduct = page.value.PreselRoast !== null 
          ? page.value.PreselRoast - 1 
          : 0
        state.active.items = initialProduct

        // Set initial variant
        const initialVariant = page.value.PreselVar !== null 
          ? page.value.PreselVar - 1 
          : 0
        state.active.variants = initialVariant

        // Set initial pack
        const initialPack = page.value.PreselPack !== null 
          ? page.value.PreselPack - 1 
          : 0
        state.active.packs = initialPack

        // Update packs and images based on selection
        if (products.value[initialProduct]?.Variants[initialVariant]) {
          state.packs = products.value[initialProduct].Variants[initialVariant].Pack.filter(
            (obj) => obj.Active
          )
          state.images = products.value[initialProduct].Variants[initialVariant].variant.Gallery[0]
        }
      }
    } catch (error) {
      console.error('Failed to initialize page:', error)
      isPageLoaded.value = false
    }
  }
  const fetchProducts = async () => {
    try {
      // Check if variant_method is enabled and fetch accordingly
      const hasVariantMethod = page.value?.ExtraData?.variant_method === true
      const variantMethodProductId = page.value?.ExtraData?.variant_method_product_id
      
      // If variant_method is enabled, fetch that specific product's variants
      if (hasVariantMethod && variantMethodProductId) {
        await runEngine.fetchProduct([variantMethodProductId])
      } else if (isSubscription.value && productsList.value.length) {
        // Standard subscription product fetch
        await runEngine.fetchProduct(productsList.value)
      } else {
        return
      }
      
      // Update stock and selling plans for loaded products
      Object.keys(state.items).forEach((key) => {
        state.items[key].Variants.forEach((variant, index) => {
          const variantFromEngine = runEngine.getVariant(variant.variant.VariantID)
          if (variantFromEngine) {
            state.items[key].Variants[index].variant.inStock = variantFromEngine.availableForSale
            if (variantFromEngine.sellingPlanAllocations) {
              state.items[key].Variants[index].variant.SellingPlans =
                variantFromEngine.sellingPlanAllocations.nodes
            }
          }
        })
      })
      
    } catch (error) {
      console.error('Failed to fetch products:', error)
    }
  }

  const FilteredPacks = computed(() => {
    return state?.items[state.active.items]?.Variants[
      state?.active?.variants
    ]?.Pack?.filter((obj) => obj.Active) || []
  })

  // Product selection methods
  const setProduct = (index) => {
    index = parseInt(index)
    state.active.items = parseInt(index)
    state.packs = products.value[index].Variants[0].Pack.filter(
      (obj) => obj.Active
    )
    state.images =
      products.value[index].Variants[0].variant.Gallery[0]
    state.active.variants = 0
    state.active.packs = parseInt(PreselectedPack.value)
  }

  const setType = (index) => {
    state.active.variants = parseInt(index)
    state.images =
      state.items[state.active.items].Variants[
        state.active.variants
      ].variant.Gallery[0]
    state.active.packs = parseInt(PreselectedPack.value)
    state.packs = state.items[state.active.items].Variants[
      state.active.variants
    ].Pack.filter((obj) => obj.Active)
  }

  const setPack = (index) => {
    state.active.packs = parseInt(index)
  }
  const setSellingPlan = (index) => {
    state.active.sellingPlan = parseInt(index)
  }
  

  watch(
    [() => isPageLoaded.value, () => hasItems.value],
    ([pageLoaded, itemsExist]) => {
      if (pageLoaded && itemsExist) {
        fetchProducts().then(() => {
          // Re-initialize preselected values after products are fetched
          if (page.value && Object.keys(products.value).length > 0) {
            const initialProduct = PreselectedItem.value
            const initialVariant = PreselectedVariant.value
            const initialPack = PreselectedPack.value

            state.active.items = initialProduct
            state.active.variants = initialVariant
            state.active.packs = initialPack

            if (products.value[initialProduct]?.Variants[initialVariant]) {
              state.packs = products.value[initialProduct].Variants[initialVariant].Pack.filter(
                (obj) => obj.Active
              )
              state.images = products.value[initialProduct].Variants[initialVariant].variant.Gallery[0]
            }
          }
        })
      }
    },
    { immediate: true }
  )


  return {
    runEngine,
    page,
    state,
    products,
    upgrades,
    Preselected: PreselectedPack,
    highlightCheckbox,
    isSubscription,
    checkboxChecked,
    FilteredPacks,
    setProduct,
    setType,
    setPack,
    setSellingPlan,
    initializePage,
  }
}